/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AddApplicationsToRoleRequest } from '../models/add-applications-to-role-request';
import { AssignPrivilegesToRoleRequest } from '../models/assign-privileges-to-role-request';
import { CreateRoleRequest } from '../models/create-role-request';
import { GenericResponse } from '../models/generic-response';
import { GetRolesResponse } from '../models/get-roles-response';
import { RoleDetailedResponse } from '../models/role-detailed-response';
import { RoleResponse } from '../models/role-response';
import { UpdateRoleRequest } from '../models/update-role-request';

@Injectable({ providedIn: 'root' })
export class RolesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllRoles()` */
  static readonly GetAllRolesPath = '/api/v1/roles';

  /**
   * Get list of roles.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllRoles()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRoles$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetRolesResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.GetAllRolesPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetRolesResponse>;
        })
      );
  }

  /**
   * Get list of roles.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllRoles$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRoles(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields. - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetRolesResponse> {
    return this.getAllRoles$Response(params, context).pipe(
      map((r: StrictHttpResponse<GetRolesResponse>): GetRolesResponse => r.body)
    );
  }

  /** Path part for operation `createRole()` */
  static readonly CreateRolePath = '/api/v1/roles';

  /**
   * Create a new role.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createRole()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRole$Response(
    params: {
      body: CreateRoleRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<RoleResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.CreateRolePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<RoleResponse>;
        })
      );
  }

  /**
   * Create a new role.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createRole$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createRole(
    params: {
      body: CreateRoleRequest;
    },
    context?: HttpContext
  ): Observable<RoleResponse> {
    return this.createRole$Response(params, context).pipe(
      map((r: StrictHttpResponse<RoleResponse>): RoleResponse => r.body)
    );
  }

  /** Path part for operation `softDeleteRole()` */
  static readonly SoftDeleteRolePath = '/api/v1/roles/{id}';

  /**
   * Soft delete role by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteRole()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteRole$Response(
    params: {
      /**
       * The role's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.SoftDeleteRolePath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Soft delete role by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `softDeleteRole$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  softDeleteRole(
    params: {
      /**
       * The role's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.softDeleteRole$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateRole()` */
  static readonly UpdateRolePath = '/api/v1/roles/{id}';

  /**
   * Update role by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateRole()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRole$Response(
    params: {
      /**
       * The role's id that needs to be updated
       */
      id: number;
      body: UpdateRoleRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<RoleResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.UpdateRolePath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<RoleResponse>;
        })
      );
  }

  /**
   * Update role by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateRole$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateRole(
    params: {
      /**
       * The role's id that needs to be updated
       */
      id: number;
      body: UpdateRoleRequest;
    },
    context?: HttpContext
  ): Observable<RoleResponse> {
    return this.updateRole$Response(params, context).pipe(
      map((r: StrictHttpResponse<RoleResponse>): RoleResponse => r.body)
    );
  }

  /** Path part for operation `assignPrivilegesToRole()` */
  static readonly AssignPrivilegesToRolePath =
    '/api/v1/roles/{role-id}/assign-privileges';

  /**
   * Assign privileges to role.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `assignPrivilegesToRole()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignPrivilegesToRole$Response(
    params: {
      /**
       * The role's id to which the privileges to be assigned
       */
      'role-id': number;
      body: AssignPrivilegesToRoleRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.AssignPrivilegesToRolePath,
      'post'
    );
    if (params) {
      rb.path('role-id', params['role-id'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Assign privileges to role.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `assignPrivilegesToRole$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  assignPrivilegesToRole(
    params: {
      /**
       * The role's id to which the privileges to be assigned
       */
      'role-id': number;
      body: AssignPrivilegesToRoleRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.assignPrivilegesToRole$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `addPrivilegesOfApplicationsToRole()` */
  static readonly AddPrivilegesOfApplicationsToRolePath =
    '/api/v1/roles/{role-id}/add-applications';

  /**
   * Add privileges of applications to role.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addPrivilegesOfApplicationsToRole()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addPrivilegesOfApplicationsToRole$Response(
    params: {
      /**
       * The role's id to which the privileges to be added
       */
      'role-id': number;
      body: AddApplicationsToRoleRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.AddPrivilegesOfApplicationsToRolePath,
      'post'
    );
    if (params) {
      rb.path('role-id', params['role-id'], {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Add privileges of applications to role.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addPrivilegesOfApplicationsToRole$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addPrivilegesOfApplicationsToRole(
    params: {
      /**
       * The role's id to which the privileges to be added
       */
      'role-id': number;
      body: AddApplicationsToRoleRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.addPrivilegesOfApplicationsToRole$Response(
      params,
      context
    ).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `getRoleById()` */
  static readonly GetRoleByIdPath = '/api/v1/roles/{role-id}';

  /**
   * Get role by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getRoleById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoleById$Response(
    params: {
      /**
       * The role's id to which the privileges to be assigned
       */
      'role-id': number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<RoleDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      RolesService.GetRoleByIdPath,
      'get'
    );
    if (params) {
      rb.path('role-id', params['role-id'], {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<RoleDetailedResponse>;
        })
      );
  }

  /**
   * Get role by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getRoleById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getRoleById(
    params: {
      /**
       * The role's id to which the privileges to be assigned
       */
      'role-id': number;
    },
    context?: HttpContext
  ): Observable<RoleDetailedResponse> {
    return this.getRoleById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<RoleDetailedResponse>): RoleDetailedResponse =>
          r.body
      )
    );
  }
}
